import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import { useNavigate } from 'react-router-dom';
import {
  clearKdsDeviceSession,
  connectKdsDevice,
  fetchKdsDeviceSession,
  getKdsDeviceSession,
} from '../utils/kdsDeviceApi';

const PIN_LENGTH = 6;
const EMPTY_PIN = Array(PIN_LENGTH).fill('');

const KdsAccess = () => {
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const submittingPinRef = useRef('');
  const [digits, setDigits] = useState(EMPTY_PIN);
  const [checkingSession, setCheckingSession] = useState(Boolean(getKdsDeviceSession()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const checkExistingSession = async () => {
      if (!getKdsDeviceSession()) {
        setCheckingSession(false);
        inputRefs.current[0]?.focus();
        return;
      }

      try {
        await fetchKdsDeviceSession();
        if (active) navigate('/kds/monitor', { replace: true });
      } catch {
        clearKdsDeviceSession();
        if (active) {
          setCheckingSession(false);
          inputRefs.current[0]?.focus();
        }
      }
    };

    checkExistingSession();
    return () => {
      active = false;
    };
  }, [navigate]);

  const resetForRetry = () => {
    submittingPinRef.current = '';
    setDigits([...EMPTY_PIN]);
    setError('');
    window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
  };

  const submitPin = async (pin) => {
    if (submitting || pin.length !== PIN_LENGTH || submittingPinRef.current === pin) return;
    submittingPinRef.current = pin;
    setSubmitting(true);
    setError('');

    try {
      await connectKdsDevice(pin);
      navigate('/kds/monitor', { replace: true });
    } catch {
      clearKdsDeviceSession();
      setError('Não foi possível conectar. Confira o PIN e tente novamente.');
      setDigits([...EMPTY_PIN]);
      submittingPinRef.current = '';
      window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  const applyDigits = (values) => {
    const next = [...EMPTY_PIN];
    values.slice(0, PIN_LENGTH).forEach((value, index) => {
      next[index] = value;
    });
    setDigits(next);
    setError('');

    const pin = next.join('');
    if (pin.length === PIN_LENGTH) {
      inputRefs.current[PIN_LENGTH - 1]?.focus();
      submitPin(pin);
    } else {
      inputRefs.current[Math.min(values.length, PIN_LENGTH - 1)]?.focus();
    }
  };

  const handleChange = (index, event) => {
    const numeric = String(event.target.value || '').replace(/\D/g, '');
    if (numeric.length > 1) {
      applyDigits(numeric.split(''));
      return;
    }

    const next = [...digits];
    next[index] = numeric;
    setDigits(next);
    setError('');

    if (numeric && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    const pin = next.join('');
    if (pin.length === PIN_LENGTH) submitPin(pin);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        setDigits(next);
        inputRefs.current[index - 1]?.focus();
      }
      submittingPinRef.current = '';
      setError('');
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const numeric = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!numeric) return;
    event.preventDefault();
    applyDigits(numeric.split(''));
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        px: 2,
        py: 4,
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 55%, #312e81 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 620,
          p: { xs: 3, sm: 5 },
          borderRadius: 5,
          textAlign: 'center',
          border: '1px solid rgba(255,255,255,0.24)',
          boxShadow: '0 24px 80px rgba(2,6,23,0.45)',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 3,
            color: '#fff',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
          }}
        >
          <RestaurantMenuRoundedIcon fontSize="large" />
        </Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
          Conectar KDS
        </Typography>
        <Typography sx={{ mt: 1, color: '#64748b' }}>
          Digite o PIN de 6 dígitos exibido na Central do KDS.
        </Typography>

        {checkingSession ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress aria-label="Verificando sessão do KDS" />
            <Typography color="text.secondary">Verificando conexão…</Typography>
          </Stack>
        ) : (
          <Box component="form" onSubmit={(event) => event.preventDefault()} sx={{ mt: 4 }}>
            <Stack
              direction="row"
              spacing={{ xs: 0.75, sm: 1.5 }}
              justifyContent="center"
              onPaste={handlePaste}
              role="group"
              aria-label="PIN de acesso ao KDS"
            >
              {digits.map((digit, index) => (
                <TextField
                  key={index}
                  value={digit}
                  onChange={(event) => handleChange(index, event)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  inputRef={(element) => { inputRefs.current[index] = element; }}
                  autoFocus={index === 0}
                  disabled={submitting}
                  error={Boolean(error)}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 1,
                    'aria-label': `Dígito ${index + 1} de ${PIN_LENGTH}`,
                    style: {
                      padding: 0,
                      textAlign: 'center',
                      fontSize: 'clamp(1.6rem, 7vw, 2.25rem)',
                      fontWeight: 800,
                    },
                  }}
                  sx={{
                    width: { xs: 44, sm: 64 },
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 58, sm: 76 },
                      borderRadius: 2.5,
                      backgroundColor: '#f8fafc',
                    },
                  }}
                />
              ))}
            </Stack>

            <Box aria-live="polite" sx={{ minHeight: 72, pt: 2 }}>
              {submitting ? (
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                  <CircularProgress size={20} />
                  <Typography color="text.secondary">Conectando à cozinha…</Typography>
                </Stack>
              ) : error ? (
                <Stack spacing={1.5} alignItems="center">
                  <Typography color="error.main" role="alert">{error}</Typography>
                  <Button variant="outlined" color="error" onClick={resetForRetry}>
                    Tentar novamente
                  </Button>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  A conexão será verificada automaticamente no sexto dígito.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default KdsAccess;
